import { useEffect, useMemo, useRef, useState } from 'react';
import type { Trip } from '@/services/trips';
import type { ItineraryItem } from '@/services/itinerary';
import { subscribeToTripItineraryItems } from '@/services/itinerary';
import { resolveItemStartInstant, resolveTripSurfaceTimezone } from '@/utils/tripItemSchedule';

type HighlightOptions = {
  windowDays?: number;
  maxTrips?: number;
};

type HighlightMap = Record<string, ItineraryItem | null>;

const findNextItem = (items: ItineraryItem[], trip: Trip, now: Date): ItineraryItem | null => {
  const timeZone = resolveTripSurfaceTimezone(trip);
  return (
    [...items]
      .filter((item) => item.status !== 'completed')
      .sort(
        (a, b) =>
          resolveItemStartInstant(a, timeZone, trip).getTime() - resolveItemStartInstant(b, timeZone, trip).getTime(),
      )
      .find((item) => resolveItemStartInstant(item, timeZone, trip).getTime() >= now.getTime()) ?? null
  );
};

export function useUpcomingItineraryHighlights(
  trips: Trip[],
  options: HighlightOptions = {},
) {
  const { windowDays = 3, maxTrips = 4 } = options;
  const [highlights, setHighlights] = useState<HighlightMap>({});
  const [loading, setLoading] = useState<boolean>(false);
  const subscriptionsRef = useRef<Array<() => void>>([]);
  const relevantTripsRef = useRef<Trip[]>([]);

  const relevantTrips = useMemo(() => {
    if (!trips || trips.length === 0) {
      return [];
    }

    const now = new Date();
    const futureThreshold = new Date(now);
    futureThreshold.setDate(futureThreshold.getDate() + windowDays);

    return trips
      .filter((trip) => !trip.isArchived)
      .filter((trip) => trip.endDate >= now && trip.startDate <= futureThreshold)
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
      .slice(0, maxTrips);
  }, [trips, windowDays, maxTrips]);

  relevantTripsRef.current = relevantTrips;

  const relevantTripsKey = useMemo(
    () =>
      relevantTrips
        .map((trip) => {
          const startAt =
            trip.startDate instanceof Date ? trip.startDate.getTime() : new Date(trip.startDate).getTime();
          const endAt =
            trip.endDate instanceof Date ? trip.endDate.getTime() : new Date(trip.endDate).getTime();
          return `${trip.id}:${startAt}:${endAt}`;
        })
        .join('|'),
    [relevantTrips],
  );

  useEffect(() => {
    subscriptionsRef.current.forEach((unsub) => {
      try {
        unsub();
      } catch {
        // no-op
      }
    });
    subscriptionsRef.current = [];

    const tripsForSubscription = relevantTripsRef.current;

    if (tripsForSubscription.length === 0) {
      setHighlights({});
      setLoading(false);
      return;
    }

    setLoading(true);
    const now = new Date();

    tripsForSubscription.forEach((trip) => {
      const unsubscribe = subscribeToTripItineraryItems(trip.id, (items) => {
        const nextItem = findNextItem(items, trip, now);
        setHighlights((prev) => {
          if (prev[trip.id]?.id === nextItem?.id) {
            return prev;
          }
          return {
            ...prev,
            [trip.id]: nextItem ?? null,
          };
        });
        setLoading(false);
      });
      subscriptionsRef.current.push(unsubscribe);
    });

    return () => {
      subscriptionsRef.current.forEach((unsub) => {
        try {
          unsub();
        } catch {
          // ignore
        }
      });
      subscriptionsRef.current = [];
    };
  }, [relevantTripsKey]);

  return {
    highlights,
    loading,
  };
}
